import { useLayoutEffect } from 'react'

/**
 * The fixed stack at the top of the page: a verify or walkthrough banner,
 * the header (the cookie notice and the mobile subscribe strip live inside
 * it) and, off phones, the subscription banner under the header. Each
 * piece's height depends on the viewport and on how its text wraps, so the
 * stack's real bottom edge is measured and published as CSS variables
 * instead of keeping a padding constant per combination (those drifted:
 * the walkthrough, cookie-notice and subscribe-strip cases each hid the top
 * of the page under the header).
 *
 * --nav-bottom: the header's bottom edge (below any banner above it); the
 *   subscription banner and the account page's mobile menu hang from it.
 * --top-stack-height: the whole stack's bottom edge; the page pads by it.
 */
export const NAV_BOTTOM_VAR = '--nav-bottom'
export const TOP_STACK_VAR = '--top-stack-height'

const NAV_STACK = '#VerifyBanner, #WalkthroughBanner, #NavBar'
const FULL_STACK = `${NAV_STACK}, #SubscriptionBanner:not(.is-mobile)`

const bottomOf = (selector) =>
  Math.max(
    0,
    ...Array.from(document.querySelectorAll(selector), (el) =>
      Math.round(el.getBoundingClientRect().bottom)
    )
  )

export const publishTopStackHeight = () => {
  const root = document.documentElement
  // the banner hangs from --nav-bottom, so set it before measuring the rest
  root.style.setProperty(NAV_BOTTOM_VAR, `${bottomOf(NAV_STACK)}px`)
  root.style.setProperty(TOP_STACK_VAR, `${bottomOf(FULL_STACK)}px`)
}

// Runs after every render of the caller (the stack's pieces mount and
// unmount with auth, route and viewport state, all of which re-render App)
// and again whenever one of them resizes, e.g. the banner's text rewrapping.
export default function useTopStackHeight() {
  useLayoutEffect(() => {
    publishTopStackHeight()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(publishTopStackHeight)
    document.querySelectorAll(FULL_STACK).forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  })
}
